import { useEffect, useState } from 'react'
import {
    View,
    Text,
    Modal,
    TextInput,
    TouchableOpacity,
    StyleSheet,
    Pressable,
    ActivityIndicator,
} from 'react-native'
import { COLORS, SPACING, RADIUS, TYPOGRAPHY } from '../constants/theme'
import { ModalStyles } from '../styles/ModalStyles'
import { ButtonStyles } from '../styles/ButtonStyles'
import { useAppTranslation } from '../i18n/useAppTranslation'
import { forgotPassword, type PasswordResetInitiatedResponse } from '../api/auth.api'

interface ForgotPasswordModalProps {
    visible: boolean
    onClose: () => void
    initialValue?: string
}

const ForgotPasswordModal = ({ visible, onClose, initialValue = '' }: ForgotPasswordModalProps) => {
    const { t } = useAppTranslation()
    const [emailOrPhone, setEmailOrPhone] = useState(initialValue)
    const [submitting, setSubmitting] = useState(false)
    const [errorMessage, setErrorMessage] = useState('')
    const [result, setResult] = useState<PasswordResetInitiatedResponse | null>(null)

    useEffect(() => {
        if (visible) {
            setEmailOrPhone(initialValue)
            setSubmitting(false)
            setErrorMessage('')
            setResult(null)
        }
    }, [visible, initialValue])

    const handleSubmit = async () => {
        const trimmed = emailOrPhone.trim()
        if (!trimmed) {
            setErrorMessage(t('auth.forgotPassword.emailOrPhoneRequired'))
            return
        }
        setErrorMessage('')
        setSubmitting(true)
        try {
            const response = await forgotPassword(trimmed)
            setResult(response)
        } catch (error: any) {
            setErrorMessage(
                error?.response?.data?.message
                    || (error instanceof Error ? error.message : String(error))
            )
        } finally {
            setSubmitting(false)
        }
    }

    const sentMessage = () => {
        const hint = result?.destinationHint
        const destination = hint
            ? t('auth.forgotPassword.sentDestination', { hint })
            : ''
        return t('auth.forgotPassword.sent', { destination })
    }

    return (
        <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
            <Pressable style={ModalStyles.overlay} onPress={onClose}>
                <View style={styles.container}>
                    <Pressable onPress={e => e.stopPropagation()} style={styles.content}>
                        <TouchableOpacity
                            style={ModalStyles.closeButton}
                            onPress={onClose}
                            activeOpacity={0.7}
                            disabled={submitting}
                        >
                            <Text style={ModalStyles.closeButtonText}>✕</Text>
                        </TouchableOpacity>

                        <Text style={ModalStyles.title}>{t('auth.forgotPassword.title')}</Text>

                        {result ? (
                            <>
                                <Text style={styles.sentText}>{sentMessage()}</Text>
                                <View style={styles.buttonsRow}>
                                    <TouchableOpacity
                                        style={[ButtonStyles.btn, ButtonStyles.btnSecondary, styles.actionBtn]}
                                        onPress={handleSubmit}
                                        disabled={submitting}
                                    >
                                        <Text style={ButtonStyles.btnSecondaryText}>
                                            {t('auth.forgotPassword.resend')}
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[ButtonStyles.btn, ButtonStyles.btnPrimary, styles.actionBtn]}
                                        onPress={onClose}
                                        disabled={submitting}
                                    >
                                        <Text style={ButtonStyles.btnPrimaryText}>
                                            {t('auth.forgotPassword.backToLogin')}
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                            </>
                        ) : (
                            <>
                                <Text style={styles.subtitle}>{t('auth.forgotPassword.subtitle')}</Text>

                                <Text style={styles.label}>{t('auth.forgotPassword.emailOrPhone')}</Text>
                                <TextInput
                                    style={styles.input}
                                    value={emailOrPhone}
                                    onChangeText={setEmailOrPhone}
                                    placeholder={t('auth.forgotPassword.emailOrPhonePlaceholder')}
                                    placeholderTextColor={COLORS.gray500}
                                    autoCapitalize="none"
                                    autoCorrect={false}
                                    editable={!submitting}
                                    keyboardType="email-address"
                                />
                                {errorMessage ? (
                                    <Text style={styles.errorText}>{errorMessage}</Text>
                                ) : null}

                                <View style={styles.buttonsRow}>
                                    <TouchableOpacity
                                        style={[ButtonStyles.btn, ButtonStyles.btnSecondary, styles.actionBtn]}
                                        onPress={onClose}
                                        disabled={submitting}
                                    >
                                        <Text style={ButtonStyles.btnSecondaryText}>
                                            {t('auth.forgotPassword.close')}
                                        </Text>
                                    </TouchableOpacity>
                                    <TouchableOpacity
                                        style={[ButtonStyles.btn, ButtonStyles.btnPrimary, styles.actionBtn]}
                                        onPress={handleSubmit}
                                        disabled={submitting}
                                    >
                                        {submitting ? (
                                            <ActivityIndicator size="small" color={COLORS.white} />
                                        ) : (
                                            <Text style={ButtonStyles.btnPrimaryText}>
                                                {t('auth.forgotPassword.submit')}
                                            </Text>
                                        )}
                                    </TouchableOpacity>
                                </View>
                            </>
                        )}
                    </Pressable>
                </View>
            </Pressable>
        </Modal>
    )
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: SPACING.lg,
    },
    content: {
        backgroundColor: COLORS.white,
        borderRadius: RADIUS.lg,
        padding: SPACING.xl,
        maxWidth: 480,
        width: '100%',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.2,
        shadowRadius: 16,
        elevation: 8,
    },
    subtitle: {
        fontSize: TYPOGRAPHY.fontSize.md,
        color: COLORS.gray700,
        marginBottom: SPACING.lg,
        includeFontPadding: false,
    },
    sentText: {
        fontSize: TYPOGRAPHY.fontSize.md,
        color: COLORS.gray700,
        marginBottom: SPACING.lg,
        includeFontPadding: false,
    },
    label: {
        fontSize: 14,
        fontWeight: '600',
        color: COLORS.form.label,
        marginBottom: SPACING.xs,
    },
    input: {
        borderWidth: 1,
        borderColor: COLORS.form.inputBorder,
        borderRadius: RADIUS.md,
        paddingHorizontal: SPACING.md,
        paddingVertical: SPACING.sm,
        fontSize: 16,
        color: COLORS.form.inputText,
        backgroundColor: COLORS.form.inputBackground,
    },
    errorText: {
        fontSize: TYPOGRAPHY.fontSize.sm,
        color: COLORS.error,
        marginTop: SPACING.xs,
        includeFontPadding: false,
    },
    buttonsRow: {
        flexDirection: 'row',
        gap: SPACING.md,
        marginTop: SPACING.lg,
    },
    actionBtn: {
        flex: 1,
    },
})

export default ForgotPasswordModal
